// @ts-nocheck

import { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import loadable from '@loadable/component';

// Actions
import api from 'app/shared/utils/api';
import { sortAreaCandidatesBySpecificity } from 'app/shared/utils/sortAreaCandidatesBySpecificity';
import AreaActions from 'app/shared/flux/actions/AreaActions';
import ConversationActions from 'app/shared/flux/actions/ConversationActions';
import FetchListingActions from 'app/shared/flux/actions/FetchListingActions';
import { FilterActions_fetchFilterFromUrlAndApi } from 'app/shared/flux/actions/FilterActions';
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';
import RouteActions from 'app/shared/flux/actions/RouteActions';
import UserActions from 'app/shared/flux/actions/UserActions';
import { selectShowHaloHdp } from 'app/shared/flux/selectors/haloExperimentSelectors';

// Components
import ListingPage from 'app/shared/pages/ListingPage';
import HaloListingPage from 'app/shared/halo/pages/HaloListingPage';
import { HaloListingPageSkeleton } from 'app/shared/halo/pages/HaloListingPage/HaloListingPageSkeleton';

// Lazy load non-critical components
const ListingNotFoundHdp = loadable(() => import('app/shared/modules/hdp/ListingNotFoundHdp'), {
  fallback: <div className="loading-skeleton" />,
});
const ServerError = loadable(() => import('app/shared/modules/ServerError'), {
  fallback: <div className="loading-skeleton" />,
});
const SkeletonHdp = loadable(() => import('app/shared/modules/skeleton/SkeletonHdp'), {
  fallback: <div className="loading-skeleton" />,
});

// Misc / Utils
import { getCurrentListingIndex } from 'app/shared/utils/listingsReducerUtils';
import constants from 'app/shared/constants/ConstantsBundle';
import controller from './controller';
import { DEFAULT } from 'app/shared/models/Filter';
import queryUtils from 'app/shared/utils/queryUtils';
import routeUtils from 'app/shared/utils/routeUtils';
import ExperimentConstants from 'app/shared/constants/ExperimentConstants';

import ErrorActions from 'app/shared/flux/actions/ErrorActions';
import AppActions from 'app/shared/flux/actions/AppActions';
import fetchListingWrapperController from 'app/shared/wrappers/FetchListingWrapper/controller';
import { MapActions_fetchStaticMap } from 'app/shared/flux/actions/MapActions/';
import { getGlobalLogger } from '@zg-rentals/logger-base';

const logger = getGlobalLogger('wrappers/FetchListingWrapper');

class FetchListingWrapper extends Component {
  constructor(props) {
    super(props);
  }
  async componentDidMount() {
    const { dispatch, pathname, query, currentListing, isClientSideLoadedPage, byCoordsListings, runningExperiments } =
      this.props;

    if (isClientSideLoadedPage) {
      try {
        // Start both operations in parallel
        const [toursResult, response = {}] = await Promise.all([
          dispatch(UserActions.hpTours.fetchScheduledToursForUser()),
          dispatch(
            FetchListingActions.chooseFetchAction({
              pathname,
              query,
            }),
          ),
        ]);

        const { listing, originalUrlWhenMakingApiCall } = response;

        if (listing && originalUrlWhenMakingApiCall === decodeURI(window.location.pathname)) {
          const isMfListing = listing.trusted;

          // Build array of parallel operations
          const parallelOperations = [
            dispatch(ListingEngineActions.setCurrentListing(listing, byCoordsListings)),
            dispatch(ConversationActions.fetchRecentMessages({ aliasEncoded: listing.aliasEncoded })),
          ];

          // Add building-specific operations if not a building
          if (!listing.building) {
            const { keyword, maloneLotIdEncoded } = controller.getListingSeoInfo({
              pathname,
              searchSlug: 'apartments-for-rent',
            });
            parallelOperations.push(
              dispatch(ListingEngineActions.loadListingSeoLinks(maloneLotIdEncoded, keyword)),
              dispatch(FetchListingActions.fetchListingDetails(listing)),
              dispatch(
                FetchListingActions.fetchSimilarListings({
                  maloneLotIdEncoded: listing.maloneLotIdEncoded,
                  unit: listing.unit,
                  listingTypes: listing.listingType,
                  limit: 8,
                }),
              ),
            );
          }

          // Add MF-specific operation if applicable
          if (isMfListing) {
            parallelOperations.push(dispatch(FetchListingActions.fetchProviderListingsPortfolio(listing.aliasEncoded)));
          }

          const hdpPopularPetsCollectionsExperiment =
            runningExperiments?.[ExperimentConstants.HOTPADS_WEB_POPULAR_PET_HDP_COLLECTIONS];
          const isExperimentOn = hdpPopularPetsCollectionsExperiment?.isControl === false;

          if (isExperimentOn) {
            parallelOperations.push(
              dispatch(FetchListingActions.fetchPetFriendlyListingsNearCurrent({ currentListing: listing })),
            );
            parallelOperations.push(
              dispatch(FetchListingActions.fetchMostPopularListingsNearCurrent({ currentListing: listing })),
            );
          }

          // Execute all operations in parallel
          await Promise.all(parallelOperations);

          return true;
        } else {
          const indexInList = getCurrentListingIndex(byCoordsListings, currentListing);
          dispatch(ListingEngineActions.setCurrentListingIndex(indexInList));
          return true;
        }
      } catch (error) {
        console.error('Error loading listing data:', error);
        // Could dispatch an error action here if needed
        return false;
      }
    } else {
      dispatch(FilterActions_fetchFilterFromUrlAndApi(pathname, query));
    }
  }

  componentDidUpdate(prevProps) {
    const { dispatch, isClientSideLoadedPage, byCoordsListings, currentListing } = this.props;

    // on SSR list isn't populated
    // if SSR, and byCoords listings get populated, set the index in the list
    if (
      !isClientSideLoadedPage &&
      isEmpty(prevProps.byCoordsListings) &&
      !isEmpty(byCoordsListings) &&
      currentListing &&
      !currentListing.belongsToMultipleUnitBuilding
    ) {
      const indexInList = getCurrentListingIndex(byCoordsListings, currentListing);

      dispatch(ListingEngineActions.setCurrentListingIndex(indexInList));
    }
  }

  componentWillUnmount = () => {
    const { dispatch, isMobile } = this.props;
    // HPWEB-6337 ToDo: uncomment once we bump react-redux package.
    // dispatch(ListingEngineActions.clearCurrentListing());
    if (!isMobile) {
      dispatch(ListingEngineActions.clearActiveMarkerAndPreviewListing());
    }
  };
  serverRouteWillMount(reactContext) {
    const trace = reactContext.trace;
    const dispatch = reactContext.store.dispatch;
    const pathname = reactContext.state.pathname;
    const query = reactContext.state.query;
    const listingTypes = DEFAULT.rentalTypes;
    const maloneLotIdEncoded = routeUtils.getMaloneLotIdEncodedFromUrl(pathname);
    const unit = routeUtils.getMaloneUnitFromUrl(pathname);
    const { keyword } = fetchListingWrapperController.getListingSeoInfo({
      pathname,
      searchSlug: 'apartments-for-rent',
    });
    let listing;
    trace.startAsyncSpan({
      marker: 'FetchListingWrapper#FetchListingActions#chooseFetchAction',
    });

    const getSeoLinks = dispatch(ListingEngineActions.loadListingSeoLinks(maloneLotIdEncoded, keyword)).catch(
      (error) => {
        dispatch(
          ErrorActions.errorHandler({
            error,
            errorLocation: 'component.listingPage.serverRouteWillMount#loadListingSeoLinks',
            errorClass: 'listingEngineActions',
          }),
        );
      },
    );
    const getListing = dispatch(FetchListingActions.chooseFetchAction({ pathname, query }))
      .then((response) => {
        // todo: move to an action
        listing = response.listing;

        if (listing) {
          if (listing.aliasEncoded && listing.active) {
            dispatch(AppActions.sendEventToApi(constants.LISTING_VIEWED, listing.aliasEncoded));
          } else if (listing.aliasEncoded && !listing.active) {
            dispatch(AppActions.sendEventToApi(constants.EXPIRED_LISTING_VIEWED, listing.aliasEncoded));
          }

          // Start parallel operations that don't depend on each other
          const parallelOperations = [];

          // Fetch scheduled tours (independent)
          parallelOperations.push(
            dispatch(UserActions.hpTours.fetchScheduledToursForUser()).catch((error) => {
              dispatch(
                ErrorActions.errorHandler({
                  error,
                  errorLocation: 'component.fetchListingWrapper.serverRouteWillMount#fetchScheduledToursForUser',
                  errorClass: 'userActions',
                }),
              );
              return null;
            }),
          );

          // Set current listing (independent)
          parallelOperations.push(
            dispatch(ListingEngineActions.setCurrentListing(listing)).catch((error) => {
              dispatch(
                ErrorActions.errorHandler({
                  error,
                  errorLocation: 'component.fetchListingWrapper.serverRouteWillMount#setCurrentListing',
                  errorClass: 'listingEngineActions',
                }),
              );
              return null;
            }),
          );

          // Fetch recent messages (independent)
          parallelOperations.push(
            dispatch(ConversationActions.fetchRecentMessages({ aliasEncoded: listing.aliasEncoded })).catch((error) => {
              dispatch(
                ErrorActions.errorHandler({
                  error,
                  errorLocation: 'component.fetchListingWrapper.serverRouteWillMount#fetchRecentMessages',
                  errorClass: 'conversationActions',
                }),
              );
              return null;
            }),
          );

          // Fetch portfolio for MF listings (independent)
          const isMfListing = listing.trusted;
          if (isMfListing) {
            parallelOperations.push(
              dispatch(FetchListingActions.fetchProviderListingsPortfolio(listing.aliasEncoded)).catch((error) => {
                dispatch(
                  ErrorActions.errorHandler({
                    error,
                    errorLocation: 'component.fetchListingWrapper.serverRouteWillMount#fetchProviderListingsPortfolio',
                    errorClass: 'fetchListingActions',
                  }),
                );
                return null;
              }),
            );
          }

          // Handle area and map fetching (map depends on area, collections depend on area)
          let areaMapPromise = Promise.resolve(response);
          if (listing.areas && listing.areas.breadcrumbs && listing.areas.breadcrumbs.length > 0) {
            const areaToLoad = listing.areas.breadcrumbs[1] || listing.areas.breadcrumbs[0];
            areaMapPromise = dispatch(AreaActions.getAndSetAreaByResourceId(areaToLoad.resourceId))
              .then(async (areaResponse) => {
                // HP-7240: If the breadcrumb area resource ID doesn't exist in the area
                // service, reverse geocode the listing's coordinates and try each candidate
                // area, skipping the one that already failed.
                if (!areaResponse && listing.geo && listing.geo.lat && listing.geo.lon) {
                  const geoResult = await dispatch(api.area.byLatLon(listing.geo.lat, listing.geo.lon));
                  const candidates = sortAreaCandidatesBySpecificity(geoResult?.data?.areas || []);
                  for (const candidate of candidates) {
                    if (!candidate.resourceId || candidate.resourceId === areaToLoad.resourceId) continue;
                    const candidateResult = await dispatch(AreaActions.getAndSetAreaByResourceId(candidate.resourceId));
                    if (candidateResult) return candidateResult;
                  }
                  return null;
                }
                return areaResponse;
              })
              .then((areaResponse) => {
                const areaPromises = [];

                const state = reactContext.store.getState();
                const runningExperiments = state.analytics?.runningExperiments;
                const hdpPopularPetsCollectionsExperiment =
                  runningExperiments?.[ExperimentConstants.HOTPADS_WEB_POPULAR_PET_HDP_COLLECTIONS];
                const isExperimentOn = hdpPopularPetsCollectionsExperiment?.isControl === false;

                if (isExperimentOn) {
                  areaPromises.push(
                    dispatch(
                      FetchListingActions.fetchPetFriendlyListingsNearCurrent({ currentListing: listing }),
                    ).catch((error) => {
                      dispatch(
                        ErrorActions.errorHandler({
                          error,
                          errorLocation:
                            'component.fetchListingWrapper.serverRouteWillMount#fetchPetFriendlyListingsNearCurrent',
                          errorClass: 'fetchListingActions',
                        }),
                      );
                      return null;
                    }),
                  );
                  areaPromises.push(
                    dispatch(
                      FetchListingActions.fetchMostPopularListingsNearCurrent({ currentListing: listing }),
                    ).catch((error) => {
                      dispatch(
                        ErrorActions.errorHandler({
                          error,
                          errorLocation:
                            'component.fetchListingWrapper.serverRouteWillMount#fetchMostPopularListingsNearCurrent',
                          errorClass: 'fetchListingActions',
                        }),
                      );
                      return null;
                    }),
                  );
                }

                const isDesktop = reactContext.store.getState().app.device.deviceType !== 'mobile';
                if (isDesktop && areaResponse) {
                  areaPromises.push(
                    dispatch(MapActions_fetchStaticMap({ area: areaResponse, query })).catch((error) => {
                      dispatch(
                        ErrorActions.errorHandler({
                          error,
                          errorLocation: 'component.fetchListingWrapper.serverRouteWillMount#fetchStaticMap',
                          errorClass: 'mapActions',
                        }),
                      );
                      return areaResponse;
                    }),
                  );
                }

                // Wait for all promises to complete
                return Promise.all(areaPromises).then(() => areaResponse);
              })
              .catch((error) => {
                dispatch(
                  ErrorActions.errorHandler({
                    error,
                    errorLocation: 'component.fetchListingWrapper.serverRouteWillMount#getAndSetAreaByResourceId',
                    errorClass: 'areaActions',
                  }),
                );
                return response;
              });
          }
          parallelOperations.push(areaMapPromise);

          // Wait for all parallel operations to complete
          return Promise.all(parallelOperations).then(() => {
            trace.finishAsyncSpan({
              marker: 'FetchListingWrapper#FetchListingActions#chooseFetchAction',
            });
            return response;
          });
        } else if ((response.redirect && response.to) || response.changeHttpStatus) {
          trace.finishAsyncSpan({
            marker: 'FetchListingWrapper#FetchListingActions#chooseFetchAction',
          });

          // For 404s, load area from geolocation so MapWrapper has data to display
          if (response.changeHttpStatus === 404) {
            logger?.info('404 detected, attempting to load area from geolocation');
            const geolocation = reactContext.store.getState().geolocation.area;
            logger?.info('Geolocation data:', { geolocation });

            if (geolocation && geolocation.resourceId) {
              logger?.info('Loading area for resourceId:', geolocation.resourceId);
              return dispatch(AreaActions.getAndSetAreaByResourceId(geolocation.resourceId))
                .then((area) => {
                  logger?.info('Area loaded successfully for 404 page:', area);
                  return response;
                })
                .catch((error) => {
                  logger?.warn('Failed to load area from geolocation:', error);
                  return response;
                });
            } else {
              logger?.warn('No geolocation resourceId available');
            }
          }

          return response;
        } else {
          dispatch(FetchListingActions.fetchListingFail());
          dispatch(ErrorActions.sendToSplunk(response, 'No listing in FetchListingWrapper'));

          throw new Error(
            `Could not get listing from FetchListing for pathname=${pathname} query=${JSON.stringify(query)}`,
          );
        }
      })
      .catch((error) => {
        dispatch(
          ErrorActions.errorHandler({
            error,
            errorLocation: 'component.fetchListingWrapper.serverRouteWillmount#chooseFetchAction',
            errorClass: 'fetchListingActions',
            criticalType: 'dataError',
          }),
        );

        return { changeHttpStatus: 500 };
      });

    const getSimilarListings = dispatch(
      FetchListingActions.fetchSimilarListings({
        maloneLotIdEncoded,
        unit,
        listingTypes: Object.keys(listingTypes).join(','),
        limit: 8,
      }),
    ).catch((error) => {
      dispatch(
        ErrorActions.errorHandler({
          error,
          errorLocation: 'component.fetchListingWrapper.serverRouteWillMount#fetchSimilarListings',
          errorClass: 'fetchListingActions',
        }),
      );
    });

    return [getListing, getSeoLinks, getSimilarListings];
  }
  // byCoords call determined the listing was a building (based on filter)
  belongsToBuildingBasedOnFilter = () => {
    const { currentListing, byCoordsListings } = this.props;

    if (!byCoordsListings.length) {
      // short circuit logic (and warning) until byCoordsListings.listingGroups.byCoords is set
      return false;
    }

    const matchedListingInList = byCoordsListings.filter((summary) => {
      return summary.maloneLotIdEncoded === currentListing.maloneLotIdEncoded;
    });

    if (matchedListingInList.length === 0) {
      return false;
    } else {
      return matchedListingInList[0].building;
    }
  };

  getBackUrl = () => {
    const { currentListing, dispatch } = this.props;
    const belongsToMultipleUnitBuilding = currentListing && currentListing.belongsToMultipleUnitBuilding;

    // byCoords and byMaloneLotId agree that the unit comes from a building AND we aren't on a building page
    if (belongsToMultipleUnitBuilding && !currentListing.building && this.belongsToBuildingBasedOnFilter()) {
      return dispatch(
        RouteActions.buildListingPathWithMap({
          listingUri: currentListing.uriBuilding,
          keepMapLocation: true,
        }),
      );
    } else {
      return dispatch(RouteActions.getAreaPathWithMapData());
    }
  };

  handlePopupClose = () => {
    const backUrl = this.getBackUrl();
    window.router.transitionTo(backUrl);
  };

  renderElement = () => {
    const {
      fetchListingFailed,
      currentListing,
      showHaloHdp,
      sidebarId,
      similarListings,
      indexInList,
      byCoordsListings,
    } = this.props;
    const listOfHdpUrls = byCoordsListings.map((summary) => summary.uriV2);

    // When HALO HDP experiment variant is active, route to the new Halo HDP instead of the legacy listing page
    if (showHaloHdp) {
      if (fetchListingFailed === constants.LISTING_NOT_FOUND) {
        return <ListingNotFoundHdp listings={similarListings} />;
      } else if (fetchListingFailed && !currentListing) {
        return <ServerError listings={similarListings} />;
      } else if (!currentListing) {
        return <HaloListingPageSkeleton />;
      } else {
        return <HaloListingPage sidebarId={sidebarId} />;
      }
    }

    // Original listing page logic
    if (fetchListingFailed === constants.LISTING_NOT_FOUND) {
      return <ListingNotFoundHdp listings={similarListings} />;
    } else if (fetchListingFailed && !currentListing) {
      return <ServerError listings={similarListings} />;
    } else if (!currentListing) {
      return <SkeletonHdp indexInList={indexInList} listOfHdpUrls={listOfHdpUrls} />;
    } else {
      return <ListingPage sidebarId={sidebarId} />;
    }
  };

  render() {
    return this.renderElement();
  }
}

const mapStateToProps = (state, ownProps) => ({
  byCoordsListings: state.listings.listingGroups.byCoords,
  currentListing: state.currentListingDetails.currentListing,
  fetchListingFailed: state.fetchListing.fetchListingFailed,
  showHaloHdp: selectShowHaloHdp(state),
  indexInList: state.currentListingDetails.indexInList,
  isClientSideLoadedPage: state.app.isClientSideLoadedPage,
  isMobile: state.app.device.screenWidth === 'sm',
  pathname: ownProps.location.pathname,
  query: queryUtils.parse(ownProps.location.search),
  runningExperiments: state.analytics?.runningExperiments,
});

export default withRouter(connect(mapStateToProps)(FetchListingWrapper));
